import { overlayRoot } from './columnScale';
import { dieArt } from './diceArt';
import { registerEscape } from './keyboard';
import type { ScreenCtx } from './types';

/**
 * Encart de lore au premier départ en Expédition (COMMENTS v0.8.0 §1.1,
 * DECISIONS §G A33) : « une fois par sauvegarde, avant le combat » — overlay
 * plein écran, chrono en attente, un toucher pour continuer.
 *
 * Le cœur livre `state.stats.loreSeen: boolean` (persisté) et
 * `game.markLoreSeen()` en parallèle de cette passe : tout est **détecté à
 * l'exécution**. Tant que le drapeau n'existe pas, `isLoreSeen` renvoie `true`
 * (DECISIONS §G : « considérer vrai pour ne rien casser ») et l'encart ne
 * s'affiche jamais — comportement actuel inchangé.
 */

/** Titre sobre de l'encart. */
const LORE_TITLE = 'Le dernier dé Maître';

/**
 * Texte **exact** de COMMENTS.md v0.8.0 §1.1, orthographe corrigée
 * (« devasté » → « dévasté ») et « Maître » mis en gras comme demandé.
 */
const LORE_HTML =
  "Le royaume est dévasté par une corruption grandissante en provenance du château. " +
  "Dans son village, l'ancien Dice Roller est à la retraite, il vous fait don du dernier dé " +
  '<strong>Maître</strong> en sa possession et vous partez à l\'aventure pour sauver votre village.';

/**
 * Garde-fou **local à cette page** (pas de persistance) : une fois l'encart
 * fermé, on ne le redemande jamais dans la même session, même si la
 * sauvegarde n'a pas encore fini de s'écrire (autosave débouncée à 400 ms,
 * `src/main.ts`) et que `state.stats.loreSeen` n'est donc pas encore relu à
 * vrai par un appel suivant.
 */
let sessionSeen = false;
/** Un seul encart affiché à la fois (les deux points d'appel — titre et
 * Accalmie — ne peuvent normalement pas se chevaucher, mais on se protège). */
let overlayOpen = false;

interface LoreApi {
  markLoreSeen?: () => void;
}

function isLoreSeen(ctx: ScreenCtx): boolean {
  if (sessionSeen) return true;
  const raw = (ctx.game.state as unknown as { stats?: { loreSeen?: unknown } }).stats?.loreSeen;
  // Drapeau absent (cœur pas encore livré) ou explicitement vrai : considéré vu.
  return raw !== false;
}

function markLoreSeen(ctx: ScreenCtx): void {
  sessionSeen = true;
  const fn = (ctx.game as unknown as LoreApi).markLoreSeen;
  if (typeof fn !== 'function') return;
  try {
    fn.call(ctx.game);
  } catch {
    /* le cœur n'a pas encore stabilisé l'API : on ne bloque jamais l'UI */
  }
}

function openLoreOverlay(ctx: ScreenCtx, onClose: () => void): void {
  overlayOpen = true;
  const overlay = document.createElement('div');
  // v0.16.0 (refonte F1, lot 3) : voile et panneau facetté à filet d'or, petit
  // dé Maître en emblème, texte en Alegreya Sans, invite « Touchez pour
  // continuer » (tout l'encart se ferme au toucher, bouton compris).
  overlay.className = 'f-scrim lore-overlay';
  overlay.setAttribute('data-role', 'lore-overlay');
  overlay.innerHTML = `
    <div class="f-modal lore-modal" role="dialog" aria-modal="true" aria-labelledby="lore-title">
      <div class="lore-emblem">${dieArt(6, 'legendary', { size: 52, stroke: 2.8 })}</div>
      <h2 class="f-modal-title lore-title" id="lore-title">${LORE_TITLE}</h2>
      <div class="f-rule" aria-hidden="true"></div>
      <p class="lore-text">${LORE_HTML}</p>
      <button type="button" class="f-btn f-btn--primary f-btn--block" data-action="lore-continue">Partir à l'aventure</button>
      <p class="lore-hint">Touchez pour continuer</p>
    </div>
  `;

  let closed = false;
  let unregisterEscape = (): void => {};
  const close = (): void => {
    if (closed) return;
    closed = true;
    unregisterEscape();
    overlayOpen = false;
    overlay.remove();
    markLoreSeen(ctx);
    onClose();
  };
  // « Toucher n'importe où ferme aussi » (bouton compris, le clic y remonte).
  overlay.addEventListener('click', close);

  overlayRoot().appendChild(overlay);
  // `Échap` ferme comme un toucher (phase 2, A78).
  unregisterEscape = registerEscape(overlay, close);
}

/**
 * Point d'entrée : affiche l'encart si nécessaire puis appelle `onProceed`
 * (les appels qui démarrent réellement la run/l'Expédition) — à la fermeture
 * s'il a été montré, immédiatement sinon. Le chrono ne tourne donc jamais
 * pendant la lecture : `onProceed` est ce qui fait passer la phase de jeu.
 */
export function maybeShowLore(ctx: ScreenCtx, onProceed: () => void): void {
  if (overlayOpen || isLoreSeen(ctx)) {
    onProceed();
    return;
  }
  openLoreOverlay(ctx, onProceed);
}
